/**
 * Dal video parte solo l'audio.
 *
 * PERCHE'
 *
 * Una lezione filmata da 400 MB e' per il 95% immagini che nessuno guarda:
 * il server la scarica da Storage, la normalizza e ne trascrive l'audio. Qui
 * l'audio si stacca PRIMA di caricare, con ffmpeg.wasm e `-vn -c:a copy`:
 * copia del flusso, senza ricodifica, quindi pochi secondi anche su un file
 * enorme. Quei 400 MB diventano ~20 MB di audio, che poi seguono la strada di
 * sempre (Storage sopra i 20 MiB, base64 sotto).
 *
 * SOLO SUL DESKTOP
 *
 * Il core di ffmpeg.wasm pesa ~32 MB e il file va montato in memoria: su un
 * telefono e' troppo, e la connessione mobile si mangerebbe il risparmio nel
 * download del core. Sul mobile il video sale intero, come prima.
 *
 * INTERRUTTORE
 *
 * `REACT_APP_VIDEO_AUDIO_LOCALE=false` spegne tutto senza toccare il codice.
 * Il default e' acceso. Se ffmpeg non parte, o il video non ha una traccia
 * audio che si possa copiare cosi' com'e', chi chiama riceve un errore e fa
 * salire il video: nessuna strada nuova fallisce senza un ripiego.
 *
 * VERSIONE
 *
 * `@ffmpeg/ffmpeg` 0.12 (`new FFmpeg()`), non la 0.9 (`createFFmpeg`) che
 * stava in package.json: quella, col core 0.10, e' la build multithread e
 * pretende SharedArrayBuffer, cioe' gli header COOP/COEP che il sito non
 * manda e che romperebbero i popup di Firebase Auth e Stripe. Il core 0.12
 * single-thread non li chiede. Il core e il worker vengono da `public/ffmpeg`
 * (copiati da `scripts/copia-pdfjs.js`), non da un CDN.
 */

export const VIDEO_AUDIO_LOCALE_ATTIVO = process.env.REACT_APP_VIDEO_AUDIO_LOCALE !== 'false';

const BASE_FFMPEG = '/ffmpeg';

// Il codec che ffmpeg trova nel video e il contenitore in cui si puo' copiare
// senza ricodificare. Il mime e' quello che il server usa per l'estensione.
const CONTENITORI = {
  aac: { estensione: 'm4a', mime: 'audio/mp4' },
  mp3: { estensione: 'mp3', mime: 'audio/mpeg' },
  opus: { estensione: 'ogg', mime: 'audio/ogg' },
  vorbis: { estensione: 'ogg', mime: 'audio/ogg' },
  pcm_s16le: { estensione: 'wav', mime: 'audio/wav' },
  pcm_s24le: { estensione: 'wav', mime: 'audio/wav' },
  pcm_s32le: { estensione: 'wav', mime: 'audio/wav' },
  pcm_f32le: { estensione: 'wav', mime: 'audio/wav' },
};

/** Dove si puo' copiare questo codec, o null se va ricodificato (e allora non qui). */
export const contenitorePerCodec = (codec) => CONTENITORI[String(codec || '').toLowerCase()] || null;

/**
 * Il codec del primo flusso audio nei log di `ffmpeg -i`. La riga ha la forma
 *   Stream #0:1(und): Audio: aac (LC) (mp4a / 0x6134706D), 48000 Hz, ...
 * @param {string[]} righe
 * @returns {string|null}
 */
export const codecAudioDaiLog = (righe) => {
  for (const riga of righe || []) {
    const trovato = /Stream #\d+:\d+.*?:\s*Audio:\s*([a-z0-9_]+)/i.exec(riga);
    if (trovato) return trovato[1].toLowerCase();
  }
  return null;
};

/**
 * Desktop e non mobile. L'iPad con Safari si presenta come Macintosh: lo si
 * riconosce dal touch.
 */
export const eDesktop = (nav = typeof navigator !== 'undefined' ? navigator : null) => {
  if (!nav) return false;
  const ua = nav.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua)) return false;
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1) return false;
  return true;
};

const eVideo = (file) => String(file?.type || '').split(';')[0].trim().toLowerCase().startsWith('video/');

/** Vero se questo file e' un video da cui, qui, si puo' staccare l'audio. */
export const puoStaccareLAudio = (file, { attivo = VIDEO_AUDIO_LOCALE_ATTIVO, desktop = eDesktop() } = {}) => {
  if (!attivo || !desktop) return false;
  if (!eVideo(file)) return false;
  return typeof WebAssembly !== 'undefined' && typeof Worker !== 'undefined';
};

const senzaEstensione = (nome) => String(nome || 'video').replace(/\.[^.]+$/, '');

// Nel file system virtuale di ffmpeg i nomi con spazi o accenti danno noia:
// l'input si chiama sempre cosi', l'output prende solo l'estensione.
const NOME_INPUT = 'input';

/**
 * L'audio del video, come File, senza ricodifica.
 *
 * @param {File} file il video
 * @param {{onProgress?: Function}} [opzioni]
 * @returns {Promise<File>}
 * @throws se ffmpeg non parte o il video non ha una traccia copiabile
 */
export const staccaTracciaAudio = async (file, { onProgress } = {}) => {
  const avvisa = (progress, message) => {
    if (typeof onProgress === 'function') onProgress({ phase: 'extracting', progress, message });
  };

  avvisa(2, 'Preparazione del motore audio...');
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const ffmpeg = new FFmpeg();
  const righe = [];
  ffmpeg.on('log', ({ message }) => righe.push(message));
  ffmpeg.on('progress', ({ progress }) => {
    if (Number.isFinite(progress)) avvisa(10 + Math.round(Math.max(0, Math.min(1, progress)) * 85), 'Estrazione della traccia audio...');
  });

  // Tre URL assoluti sull'origine del sito. `classWorkerURL` e' il worker di
  // classe della libreria servito raw da public/ (vedi scripts/copia-pdfjs.js):
  // dentro fa `import(coreURL)`, che dal bundle di webpack non funzionerebbe.
  const origine = typeof window !== 'undefined' ? window.location.origin : '';
  await ffmpeg.load({
    coreURL: `${origine}${BASE_FFMPEG}/ffmpeg-core.js`,
    wasmURL: `${origine}${BASE_FFMPEG}/ffmpeg-core.wasm`,
    classWorkerURL: `${origine}${BASE_FFMPEG}/worker.js`,
  });

  const puntoDiMontaggio = '/video';
  const percorsoInput = `${puntoDiMontaggio}/${NOME_INPUT}`;
  let montato = false;
  try {
    avvisa(8, 'Lettura del video...');
    // WORKERFS legge dal File a pezzi, senza copiarlo tutto in memoria: e' la
    // differenza fra staccare l'audio da 400 MB e far crollare la scheda.
    // L'input va montato col nome fisso: WORKERFS espone il File col suo
    // `name`, quindi gli si da' una copia rinominata senza copiare i byte.
    const rinominato = new File([file], NOME_INPUT, { type: file.type });
    await ffmpeg.createDir(puntoDiMontaggio);
    await ffmpeg.mount('WORKERFS', { files: [rinominato] }, puntoDiMontaggio);
    montato = true;

    // Prima passata: solo per sapere che codec c'e' dentro. Esce con errore
    // ("At least one output file must be specified"), ed e' voluto.
    await ffmpeg.exec(['-hide_banner', '-i', percorsoInput]);
    const codec = codecAudioDaiLog(righe);
    const contenitore = contenitorePerCodec(codec);
    if (!contenitore) {
      throw new Error(codec ? `Traccia audio ${codec} non copiabile senza ricodifica` : 'Nessuna traccia audio nel video');
    }

    const nomeOutput = `audio.${contenitore.estensione}`;
    // Per l'm4a l'indice (moov) va in testa: cosi' chi lo riceve puo' leggerlo
    // in streaming senza aspettare la fine del file.
    const opzioniMuxer = contenitore.estensione === 'm4a' ? ['-movflags', '+faststart'] : [];
    const esito = await ffmpeg.exec(['-hide_banner', '-i', percorsoInput, '-vn', '-c:a', 'copy', ...opzioniMuxer, '-y', nomeOutput]);
    if (esito !== 0) {
      throw new Error(`ffmpeg ha risposto ${esito} durante la copia dell'audio`);
    }

    const byte = await ffmpeg.readFile(nomeOutput);
    await ffmpeg.deleteFile(nomeOutput).catch(() => {});
    if (!byte || !byte.length) {
      throw new Error('La traccia audio e\' uscita vuota');
    }

    avvisa(100, 'Traccia audio pronta');
    return new File([byte], `${senzaEstensione(file.name)}.${contenitore.estensione}`, {
      type: contenitore.mime,
      lastModified: file.lastModified || Date.now(),
    });
  } finally {
    if (montato) await ffmpeg.unmount(puntoDiMontaggio).catch(() => {});
    ffmpeg.terminate();
  }
};

const tracciaAudio = {
  VIDEO_AUDIO_LOCALE_ATTIVO,
  contenitorePerCodec,
  codecAudioDaiLog,
  eDesktop,
  puoStaccareLAudio,
  staccaTracciaAudio,
};

export default tracciaAudio;
